/**
 * kdsMerge.js — pure transforms behind the live KDS board.
 *
 * `mergeTicket` folds a Square order together with its Firestore check state
 * (and any optimistic cross-tab overlay, pre-merged by the caller) into the
 * unified ticket shape the board renders. `deriveTicketStatus` classifies a
 * merged ticket against the current clock. Both are pure and unit-tested — this
 * is the logic where the snapshot/fire-time bugs originally lived.
 */
import { deriveSnapshotCount, buildItemFireTimes, computeActiveElapsed } from '../ticketTimingUtils';
import { tsToLocal } from './dateUtils';
import { normalizeTimeMap } from './prepUtils';
import { STALE_MINUTES } from '../ticketConstants';

/**
 * @param {Object} order — Square order ({ id, items, createdAt, state, ... })
 * @param {Object} ck    — combined ticketChecks + overlay for this order
 * @param {number} nowMs
 * @returns {Object} merged ticket
 */
export function mergeTicket(order, ck, nowMs) {
  const o = order;
  const c = ck || {};
  const checkedItems = c.checkedItems || [];
  const assignedItems = Array.isArray(c.assignedItems) ? c.assignedItems : [];
  const chefDoneItems = Array.isArray(c.chefDoneItems) ? c.chefDoneItems : [];
  const manuallyCompleted = !!c.manuallyCompleted;
  const assigned = !!c.assigned || assignedItems.length > 0;

  // Snapshot count = items that existed at FIRST completion (0 = never completed).
  // Must not grow as items are checked mid-service, or unchecked originals get
  // misclassified as "new" and their fire time resets.
  const snapshotCount = deriveSnapshotCount({
    itemsSnapshot: c.itemsSnapshot,
    manuallyCompleted,
    checkedItems: c.checkedItems,
  });
  const hasNewItems = manuallyCompleted && o.items && o.items.length > snapshotCount && snapshotCount > 0;
  const isReopened = !!c.reopenedForNewItems || hasNewItems;
  const reopenedAt = c.reopenedAt ? tsToLocal(c.reopenedAt) : null;
  const createdMs = o.createdAt ? new Date(o.createdAt).getTime() : nowMs;

  const itemFireTimes = buildItemFireTimes({
    itemCount: (o.items || []).length,
    createdAtMs: createdMs,
    snapshotCount,
    existingFireTimes: c.itemFireTimes || null,
    reopenedAtMs: reopenedAt ? reopenedAt.getTime() : null,
    itemCheckedMs: c.itemCheckedMs || null,
  });

  const activeMins = computeActiveElapsed({ itemFireTimes, checkedItems, itemCheckedMs: c.itemCheckedMs || {}, nowMs });

  let status;
  if (hasNewItems) status = 'open';
  else if (manuallyCompleted) status = 'completed';
  else if (activeMins >= STALE_MINUTES) status = 'stale';
  else if (assigned || checkedItems.length > 0 || chefDoneItems.length > 0) status = 'inprogress';
  else status = 'open';

  return {
    ...o, squareState: o.state, checkedItems, chefDoneItems,
    completedAt: c.completedAt || null, timeTakenMin: c.timeTakenMin || null,
    prepCompletedAt: c.prepCompletedAt || null, prepTimeMin: c.prepTimeMin ?? null,
    prepSource: c.prepSource || null, chefDoneMs: normalizeTimeMap(c.chefDoneMs),
    itemCheckedMs: c.itemCheckedMs || {}, assigned, manuallyCompleted, assignedItems, status,
    hasNewItems: !!hasNewItems, snapshotCount, reopenedForNewItems: isReopened,
    reopenedAt: reopenedAt ? reopenedAt.toISOString() : null,
    itemFireTimes,
    persistedFireCount: c.itemFireTimes ? Object.keys(c.itemFireTimes).length : 0,
    pickedUp: !!c.pickedUp, pickedUpAt: c.pickedUpAt || null,
  };
}

/** Classify a merged ticket against the current clock. */
export function deriveTicketStatus(o, nowMs) {
  const mins = computeActiveElapsed({
    itemFireTimes: o.itemFireTimes, checkedItems: o.checkedItems, itemCheckedMs: o.itemCheckedMs, nowMs,
  });
  if (o.hasNewItems) return 'open';
  if (o.manuallyCompleted) return 'completed';
  if (mins >= STALE_MINUTES) return 'stale';
  if (o.assigned || o.checkedItems.length > 0 || (o.chefDoneItems || []).length > 0) return 'inprogress';
  return 'open';
}

/**
 * Should a COMPLETED ticket still show on the live board's Completed tab?
 * The board keeps only the last `windowMin` minutes of finished work (the full
 * day lives in the History tab). Unfinished tickets never go through this — an
 * old open ticket is exactly what the Stale tab is for. A ticket with no
 * readable completion time is kept: better shown than silently hidden.
 */
export function isRecentlyCompleted(ticket, nowMs, windowMin) {
  const done = tsToLocal(ticket && ticket.completedAt);
  const ms = done ? done.getTime() : NaN;
  if (!Number.isFinite(ms)) return true;
  return nowMs - ms <= windowMin * 60000;
}
